import { getErrorMessage } from '../utils/errorHandler';
import { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, ShieldCheck } from 'lucide-react';
import Turnstile from 'react-turnstile';
import axios from 'axios';

const TURNSTILE_SITE_KEY = '0x4AAAAAACZyG0rcsEZoq7KZ';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VITALIS_LOGO = "https://vitalis-sports.ch/wp-content/uploads/2024/12/logo_web.png";

export default function Login() {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileError, setTurnstileError] = useState(false);
  const [captchaRequired, setCaptchaRequired] = useState(true);
  const turnstileRef = useRef(null);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const { login } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const navigate = useNavigate();

  // Check if CAPTCHA is required
  useEffect(() => {
    try {
      if (sessionStorage.getItem('session_expired')) {
        sessionStorage.removeItem('session_expired');
        toast.info('Votre session a expiré. Veuillez vous reconnecter.');
      }
    } catch (e) {}
    const checkCaptchaStatus = async () => {
      try {
        const res = await axios.get(`${API}/auth/captcha-status`);
        setCaptchaRequired(res.data.captcha_required);
        if (!res.data.captcha_required) {
          setTurnstileToken('disabled'); // Set a placeholder to enable login
        }
      } catch (err) {
        // Default to requiring CAPTCHA if check fails
        setCaptchaRequired(true);
      }
    };
    checkCaptchaStatus();
  }, []);

  const handleTurnstileVerify = (token) => {
    setTurnstileToken(token);
    setTurnstileError(false);
  };

  const handleTurnstileExpire = () => {
    setTurnstileToken(null);
  };

  const handleTurnstileError = () => {
    setTurnstileError(true);
    setTurnstileToken(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (captchaRequired && !turnstileToken) {
      toast.error(language === 'fr' ? 'Veuillez compléter la vérification de sécurité' : 
                  language === 'de' ? 'Bitte führen Sie die Sicherheitsüberprüfung durch' :
                  'Please complete the security verification');
      return;
    }
    
    setLoading(true);

    try {
      // Only pass turnstile token if CAPTCHA is required
      const tokenToSend = captchaRequired ? turnstileToken : null;
      await login(formData.email, formData.password, tokenToSend);
      toast.success(t('loginSuccess'));
      navigate('/dashboard');
    } catch (err) {
      toast.error(getErrorMessage(err, t('error')));
      // Reset Turnstile on error
      if (captchaRequired) {
        setTurnstileToken(null);
        if (turnstileRef.current) {
          turnstileRef.current.reset();
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      {/* Hero Section */}
      <div 
        className="login-hero hidden md:block"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=1200&q=80')`
        }}
      >
        <div className="relative z-10 p-12 h-full flex flex-col justify-between text-white">
          <div>
            <img src={VITALIS_LOGO} alt="Vitalis Sports" className="h-12" />
          </div>
          <div className="max-w-md">
            <h1 className="text-4xl mb-4 text-white">
            </h1>
            <p className="text-white/80">
            </p>
          </div>
        </div>
      </div>

      {/* Form Section */}
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="md:hidden flex justify-center mb-8">
            <img src={VITALIS_LOGO} alt="Vitalis Sports" className="h-12" />
          </div>

          {/* Language Selector */}
          <div className="flex justify-center gap-2 mb-8">
            {['fr', 'en', 'de'].map((lang) => (
              <button
                key={lang}
                onClick={() => setLanguage(lang)}
                className={`lang-btn ${language === lang ? 'active' : ''}`}
              >
                {lang.toUpperCase()}
              </button>
            ))}
          </div>

          <div className="mb-8">
            <h2 className="text-3xl mb-2">{t('login')}</h2>
            <p className="text-muted-foreground text-sm">
              {t('accessWorkspace')}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t('email')}</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                placeholder=""
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">{t('password')}</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  data-testid="login-password-input"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              data-testid="login-submit-btn"
              disabled={loading || (captchaRequired && !turnstileToken)}
              className="w-full"
            >
              {loading ? t('loading') : t('signIn')}
            </Button>
          </form>

          {/* Turnstile CAPTCHA - only show if required */}
          {captchaRequired && (
            <div className="mt-4 flex flex-col items-center">
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                <ShieldCheck className="h-3.5 w-3.5" />
                <span>{language === 'fr' ? 'Protection anti-bot' : language === 'de' ? 'Bot-Schutz' : 'Bot protection'}</span>
              </div>
              <Turnstile
                ref={turnstileRef}
                sitekey={TURNSTILE_SITE_KEY}
                onVerify={handleTurnstileVerify}
                onExpire={handleTurnstileExpire}
                onError={handleTurnstileError}
                theme="auto"
                refreshExpired="auto"
              />
              {turnstileError && (
                <p className="text-xs text-red-500 mt-2">
                  {language === 'fr' ? 'Erreur de vérification. Veuillez réessayer.' : 
                   language === 'de' ? 'Überprüfungsfehler. Bitte versuchen Sie es erneut.' :
                   'Verification error. Please try again.'}
                </p>
              )}
            </div>
          )}

          <div className="mt-6 text-center">
            <Link
              to="/forgot-password"
              data-testid="forgot-password-link"
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              {t('forgotPassword')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
